import { storage } from '@uhg-abyss/web/tools/storage';
import { useMemo } from 'react';

import { Constants } from '../common/Constants';
import { isEniMember } from '../common/Utils/memberUtils/memberUtils';
import { getCurrentMember } from '../utils/user.utils';
import { useLagoon } from './useLagoon';

export interface CexDeeplinkFilterMappingEntry {
  dntName: string;
  dntType: string;
  // Comma-separated PES filter keys, matching the deeplink-mapping table shape.
  dntFilters: string;
}


export interface Props {
  dntFilterKeys: string[];
  dntType?: string;
}

const normalize = (value: string): string => value.trim().toLowerCase();

const isCexDeeplinkFilterMappingEntry = (
  value: unknown
): value is CexDeeplinkFilterMappingEntry =>
  !!value &&
    typeof value === 'object' &&
    typeof (value as CexDeeplinkFilterMappingEntry).dntName === 'string' &&
    typeof (value as CexDeeplinkFilterMappingEntry).dntType === 'string' &&
    typeof (value as CexDeeplinkFilterMappingEntry).dntFilters === 'string';

const safelyGetFilterMapping = (
  lagoonTableAccessor: unknown
): CexDeeplinkFilterMappingEntry[] => {
  if (typeof lagoonTableAccessor !== 'function') {
    return [];
  }

  try {
    const tableData = lagoonTableAccessor();
    if (!Array.isArray(tableData)) {
      return [];
    }

    return tableData.filter(isCexDeeplinkFilterMappingEntry);
  } catch {
    return [];
  }
};

export const resolveCanonicalMappingEntry = (
  searchTerm: string,
  filterMapping: CexDeeplinkFilterMappingEntry[] = []
): CexDeeplinkFilterMappingEntry | undefined => {
  const normalizedSearchTerm = normalize(searchTerm);
  return filterMapping?.find(
    (entry) =>
      entry?.dntName &&
      normalize(entry.dntName) === normalizedSearchTerm
  );
};

const isEniAuthenticatedSession = (): boolean => {
  const currentMember = getCurrentMember();
  const isGuest = !!storage.session.get(
    Constants.STORAGE_KEYS.SESSION.IS_GUEST
  );
  return isEniMember(currentMember?.lineOfBusiness) && !isGuest;
};

/**
 * Resolves the CEX DME deep link `searchTerm` (CEFIND-6099) to the Lagoon-mapped
 * PES filter keys, without regard to which of those keys PES actually has
 * available — callers are responsible for cross-referencing the result
 * against their own filter options once loaded.
 *
 * Returns an empty array whenever the feature is disabled, the session isn't
 * E&I authenticated, or the searchTerm has no Lagoon mapping.
 */
export const useDeeplinkFilters = (
  params: string | null | undefined
): Props => {

  const lagoonTableAccessor = useLagoon(
    Constants.LAGOON_TABLE.DEEPLINK_MAPPING
  );
  const filterMapping: CexDeeplinkFilterMappingEntry[] =
    safelyGetFilterMapping(lagoonTableAccessor);
  const isEniAuthenticated = isEniAuthenticatedSession();
  // useLagoon returns a new array/function reference on every render, so key
  // the memo off its serialized content instead of recomputing (and
  // returning a new array) on every render.
  const filterMappingKey = useMemo(
    () => JSON.stringify(filterMapping),
    [filterMapping]
  );

  return useMemo(() => {
    if (!params || !isEniAuthenticated) {
      return { dntFilterKeys: [], dntType: undefined };
    }

    const canonicalEntry = resolveCanonicalMappingEntry(
      params,
      filterMapping
    );

    const dntFilterKeys = canonicalEntry?.dntFilters
      ?.split(',')
      .map((filterKey) => filterKey.trim())
      .filter(Boolean);

    return { dntFilterKeys: dntFilterKeys?.length ? dntFilterKeys : [],
      dntType: canonicalEntry?.dntType,
     };
  }, [
    params,
    isEniAuthenticated,
    filterMappingKey,
  ]);
};
